import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@cms/ui/components/select";
import { SOCIAL_PLATFORMS, type SocialLink } from "@cms/config";

// Social profiles are plain web addresses (mirrors apps/api/src/social-links.ts).
const SOCIAL_URL_RE = /^https:\/\/\S+$/i;

export function socialLinkError(link: SocialLink): string {
  return link.url.trim() && !SOCIAL_URL_RE.test(link.url.trim())
    ? "Links must start with https://."
    : "";
}

/** Parse a stored social_links_json string into editable rows. */
export function parseSocialLinks(raw: string | null | undefined): SocialLink[] {
  if (!raw || typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is SocialLink =>
        entry && SOCIAL_PLATFORMS.some((p) => p.slug === entry.platform) && typeof entry.url === "string"
    );
  } catch {
    return [];
  }
}

interface SocialLinksFieldProps {
  value: SocialLink[];
  onChange: (links: SocialLink[]) => void;
}

/**
 * Editable list of footer social links: one row per platform (platform
 * dropdown + profile URL). Platforms already used are hidden from other
 * rows' dropdowns so each appears at most once.
 */
export function SocialLinksField({ value, onChange }: SocialLinksFieldProps) {
  const unused = SOCIAL_PLATFORMS.filter((p) => !value.some((link) => link.platform === p.slug));

  const setRow = (index: number, patch: Partial<SocialLink>) => {
    onChange(value.map((link, i) => (i === index ? { ...link, ...patch } : link)));
  };

  return (
    <div className="space-y-2">
      <Label>Social links</Label>
      {value.map((link, index) => {
        const error = socialLinkError(link);
        return (
          <div key={link.platform} className="space-y-1">
            <div className="flex items-center gap-2">
              <Select
                value={link.platform}
                onValueChange={(value: string) => setRow(index, { platform: value as SocialLink["platform"] })}
              >
                <SelectTrigger aria-label="Platform" className="w-40 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_PLATFORMS.filter(
                    (p) => p.slug === link.platform || unused.some((u) => u.slug === p.slug)
                  ).map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={link.url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRow(index, { url: e.target.value })}
                placeholder="https://…"
                aria-label={`${link.platform} profile URL`}
                aria-invalid={error ? "true" : undefined}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                aria-label={`Remove ${link.platform} link`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        );
      })}
      {unused.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange([...value, { platform: unused[0].slug, url: "" }])}
        >
          <Plus className="h-4 w-4" />
          Add social link
        </Button>
      )}
      <p className="text-xs text-muted-foreground">
        Shown as icons in your site footer, in this order.
      </p>
    </div>
  );
}
